<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

export default {
    name: "TOCTemp7",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        toc: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
    },
    data() {
        return {
            pages: [],
            footerImage: ""
        };
    },
    watch: {
        toc: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.splitContentIntoPages();
                });
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.generateGradients();
            this.splitContentIntoPages();
        });
    },
    methods: {
        getAlphabet(index) {
            let result = "";
            index += 1;
            while (index > 0) {
                let rem = (index - 1) % 26;
                result = String.fromCharCode(65 + rem) + result;
                index = Math.floor((index - 1) / 26);
            }
            return result.toUpperCase();
        },
        splitContentIntoPages() {
            if (!this.toc || !this.toc.pages) return;

            const itemsPerPage = parseInt(this.toc.itemsPerPage);
            const projects = this.toc.projects || [];
            let allTocItems = [...this.toc.pages].map((item, index) => ({
                ...item,
                alphabet: this.getAlphabet(index)
            }));

            this.pages = [];
            let pagesProjectsIndex = 0;

            while (allTocItems.length > 0) {
                let currentPageItems = allTocItems.splice(0, itemsPerPage);

                this.pages.push({
                    leftItems: currentPageItems,
                    project: projects[pagesProjectsIndex] || null
                });
                pagesProjectsIndex++;

                if (this.pages.length > 50) break;
            }

            if (this.pages.length === 0) {
                this.pages.push({ leftItems: [], project: projects[0] || null });
            }
        },
        generateGradients() {
            const fCanvas = document.createElement('canvas');
            fCanvas.width = 1130;
            fCanvas.height = 180;
            const fCtx = fCanvas.getContext('2d');
            const fGrad = fCtx.createLinearGradient(0, 180, 0, 0);
            fGrad.addColorStop(0, 'rgba(0, 0, 0, 0.7)');
            fGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            fCtx.fillStyle = fGrad;
            fCtx.fillRect(0, 0, 1130, 180);
            this.footerImage = fCanvas.toDataURL();
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': (toc.style.colorScheme && toc.style.colorScheme.header) || '#1b5e20',
        '--body-color': (toc.style.colorScheme && toc.style.colorScheme.body) || '#ffffff',
        '--footer-color': (toc.style.colorScheme && toc.style.colorScheme.footer) || '#ffffff',
        '--heading-color': toc.style.heading.color || '#ffffff',
        '--heading-size': toc.style.heading.size + 'px',
        '--heading-weight': toc.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': toc.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': toc.style.heading.underline ? 'underline' : 'none',
        '--heading-font': toc.style.heading.font,

        '--label-color': toc.style.label.color || '#ffffff',
        '--label-size': toc.style.label.size + 'px',
        '--label-weight': toc.style.label.bold ? 'bold' : 'normal',
        '--label-style': toc.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': toc.style.label.underline ? 'underline' : 'none',
        '--label-font': toc.style.label.font,

        '--text-color': toc.style.text.color || '#ffffff',
        '--text-size': toc.style.text.size + 'px',
        '--text-weight': toc.style.text.bold ? 'bold' : 'normal',
        '--text-style': toc.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': toc.style.text.underline ? 'underline' : 'none',
        '--text-font': toc.style.text.font,
    }">
        <div class="sub-container" v-for="(page, pageIndex) in pages" :key="pageIndex">
            <div class="left-section">
                <div class="left-content" :class="pageIndex > 0 && 'mt-auto'">
                    <div v-if="pageIndex === 0" class="toc-header">
                        <div class="logo-sec photobox">
                            <PhotoBox :project="toc" type="logo" bgColor="transparent" objectFit="contain" />
                        </div>
                        <h1 class="title">
                            <SpellCheckedText :value="toc.title || 'Table of Contents'" @input="toc.title = $event" />
                        </h1>
                    </div>

                    <div class="toc-list">
                        <div v-for="(item, idx) in page.leftItems" :key="idx" class="toc-item">
                            <span class="heading">
                                <SpellCheckedText :value="item.heading || ''" @input="item.heading = $event" />
                            </span>
                            <div class="dots"></div>
                            <span class="page-num">{{ String(item.page || '').padStart(2, '0') }}</span>
                        </div>
                    </div>
                </div>

                <div class="toc-footer">
                    <p class="footer-text">
                        <SpellCheckedText :value="toc.opprName || ''" @input="toc.opprName = $event" />
                    </p>
                </div>
            </div>

            <div class="right-section right-half photobox">
                <PhotoBox v-if="page.project" :project="page.project" :bgColor="toc.style.colorScheme && toc.style.colorScheme.body"
                    position="center" objectFit="cover" />
                <div v-else class="bg-placeholder" />
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 296.83mm;
    min-height: 211mm;
    position: relative;
    box-sizing: border-box;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .sub-container {
        display: flex;
        flex-direction: row;
        width: 296.83mm;
        height: 211mm;
        overflow: hidden;

        .right-section {
            width: 45%;
            height: 100%;

            .bg-placeholder {
                width: 100%;
                height: 100%;
                background: var(--body-color);
            }
        }

        .left-section {
            display: flex;
            flex-direction: column;
            width: 55%;
            height: 100%;
            padding: 30px;
            background: var(--header-color);

            .left-content {
                display: flex;
                flex-direction: column;

                .logo-sec {
                    width: 140px;
                    height: 40px;
                    margin-bottom: 50px;
                }

                .toc-header {
                    margin-bottom: 30px;

                    .title {
                        font-size: var(--heading-size);
                        color: var(--heading-color);
                        font-family: var(--heading-font);
                        font-weight: var(--heading-weight);
                        font-style: var(--heading-style);
                        text-decoration: var(--heading-decoration);
                        margin: 0;
                    }
                }

                .toc-list {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;

                    .toc-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 15px;

                        .heading {
                            font-size: var(--text-size);
                            color: var(--text-color);
                            font-family: var(--text-font);
                            font-weight: var(--text-weight);
                            font-style: var(--text-style);
                            text-decoration: var(--text-decoration);
                            line-height: 1.2;
                            min-width: 0;
                        }

                        .dots {
                            flex: 1;
                        }

                        .page-num {
                            font-size: var(--label-size);
                            color: var(--label-color);
                            font-family: var(--label-font);
                            font-weight: var(--label-weight);
                            font-style: var(--label-style);
                            text-decoration: var(--label-decoration);
                            min-width: 25px;
                            text-align: right;
                        }
                    }
                }
            }

            .toc-footer {
                margin-top: auto;

                .footer-text {
                    font-size: 14px;
                    font-weight: bold;
                    color: var(--footer-color);
                    margin: 0;
                }
            }
        }
    }
}
</style>
